import React, { useEffect, useState } from 'react';
import { UserCircle, Phone, Mail, CreditCard, Car } from 'lucide-react';
import { motion } from 'framer-motion';
import { API_URL, API_URL_UPLOADS } from './config';
import DashboardLayout from '../components/DashboardLayout/DasboardLayout';

const DriverList = () => {
    const [drivers, setDrivers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');

    useEffect(() => {
        const fetchDrivers = async () => {
            try {
                const response = await fetch(`${API_URL}/driver`);
                if (!response.ok) {
                    throw new Error('Network response was not ok');
                }
                const data = await response.json();
                setDrivers(data);
            } catch (error) {
                setError(error.message);
            } finally {
                setLoading(false);
            }
        };

        fetchDrivers();
    }, []);

    const filteredDrivers = drivers.filter(driver => {
        const fullName = `${driver.name} ${driver.surname}`.toLowerCase();
        return fullName.includes(searchQuery.toLowerCase()) || 
               driver.idnumber.toLowerCase().includes(searchQuery.toLowerCase());
    });

    if (loading) return (
        <DashboardLayout>
            <div className="min-h-screen flex items-center justify-center">
                <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-[#ffc000]"></div>
            </div>
        </DashboardLayout>
    );

    if (error) return (
        <DashboardLayout>
            <div className="min-h-screen flex items-center justify-center">
                <div className="bg-red-50 p-6 rounded-lg shadow-lg">
                    <p className="text-red-500 text-center text-lg">{error}</p>
                </div>
            </div>
        </DashboardLayout>
    );

    return (
        <DashboardLayout>
            <div className="min-h-screen bg-gradient-to-b from-white to-gray-50 py-12 px-4">
                <div className="max-w-4xl mx-auto">
                    <motion.h2 
                        initial={{ y: -20, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        className="text-3xl font-bold text-center mb-8 text-[#002966]"
                    >
                        Driver List
                    </motion.h2>
                    
                    <div className="mb-6">
                        <input 
                            type="text"
                            placeholder="Search by name or ID"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="w-full p-3 border border-gray-300 rounded"
                        />
                    </div>

                    <div className="space-y-6">
                        {filteredDrivers.map((driver, index) => (
                            <motion.div
                                initial={{ x: -20, opacity: 0 }}
                                animate={{ x: 0, opacity: 1 }}
                                transition={{ delay: index * 0.1 }}
                                key={driver.driver_id}
                                className="bg-[#002966] rounded-xl shadow-lg hover:shadow-xl transition-shadow duration-300 overflow-hidden"
                            >
                                <div className="p-6">
                                    <div className="flex flex-col md:flex-row md:items-center gap-6">
                                        <div className="relative">
                                            <div className="w-32 h-32 rounded-full overflow-hidden border-4 border-[#ffc000] shadow-lg">
                                                <img 
                                                    src={`${API_URL_UPLOADS}/${driver.profilePic}`} 
                                                    alt={`${driver.name} ${driver.surname}`} 
                                                    className="w-full h-full object-cover"
                                                />
                                            </div>
                                            <div className="absolute bottom-0 right-0 bg-[#ffc000] text-[#002966] px-3 py-1 rounded-full text-sm">
                                                {driver.membershipstatus}
                                            </div>
                                        </div>
                                        
                                        <div className="flex-1 space-y-3">
                                            <h3 className="text-2xl font-semibold text-white">
                                                {driver.name} {driver.surname}
                                            </h3>
                                            
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                                <div className="flex items-center gap-2 text-white">
                                                    <UserCircle className="w-5 h-5 text-white" />
                                                    <span className='text-white'>ID: {driver.idnumber}</span>
                                                </div>
                                                
                                                <div className="flex items-center gap-2 text-gray-600">
                                                    <Phone className="w-5 h-5 text-white" />
                                                    <span className='text-white'>{driver.phone || 'N/A'}</span>
                                                </div>
                                                
                                                <div className="flex items-center gap-2 text-gray-600">
                                                    <Car className="w-5 h-5 text-[#ffc000]" />
                                                    <span className='text-white'>Plate: {driver.plate}</span><br></br>
                                                    <span className='text-white'>Driving: {driver.make}{" "}{driver.model}{" "}{driver.year}{" "}{driver.color}</span>

                                                </div>
                                                
                                                <div className="flex items-center gap-2 text-gray-600">
                                                    <Mail className="w-5 h-5 text-white" />
                                                    <span className='text-white'>{driver.email || 'N/A'}</span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </div>
        </DashboardLayout>
    );
};

export default DriverList;